import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useState } from 'react'
import { RequestError, api } from '../../lib/api'
import { AfraMark } from '../../design/AfraMark'

/**
 * The only public page. There is no registration: an admin creates every
 * account and hands over the email and password, so this is just a door.
 */
export function AuthPage() {
  return (
    <AuthShell>
      <LoginForm />
      <p className="meta" style={{ marginTop: 'var(--space-4)' }}>
        حساب ندارید؟ حساب را افرا برایتان می‌سازد.
      </p>
    </AuthShell>
  )
}

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="container" style={{ maxWidth: 520, paddingTop: 'var(--space-7)' }}>
      <div className="center" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="auth__brand"><AfraMark /><h1>افرا</h1></div>
        <p className="muted">
          دستیار ارزیابی و توسعه سازمانی برای مؤسسات خیریه و سازمان‌های مردم‌نهاد
        </p>
      </div>
      <div className="card">{children}</div>
    </div>
  )
}

function useSubmit() {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await queryClient.invalidateQueries({ queryKey: ['me'] })
    } catch (err) {
      setError(err instanceof RequestError ? err.messageFa : 'خطای ناشناخته‌ای رخ داد.')
    } finally {
      setBusy(false)
    }
  }

  return { run, error, busy }
}

function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { run, error, busy } = useSubmit()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void run(() => api.login(email, password))
  }

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label htmlFor="login-email">ایمیل</label>
        <input id="login-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="login-password">گذرواژه</label>
        <input id="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      {error && <div className="notice notice--error" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}
      <button type="submit" className="btn" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'در حال ورود…' : 'ورود'}
      </button>
    </form>
  )
}

/**
 * Shown instead of the app until an admin-issued password is replaced. The
 * server refuses everything else in the meantime; this is only the screen that
 * says so.
 */
export function ChangePasswordPage({ onSignOut }: { onSignOut: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [mismatch, setMismatch] = useState(false)
  const { run, error, busy } = useSubmit()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (next !== repeat) {
      setMismatch(true)
      return
    }
    setMismatch(false)
    void run(() => api.changePassword(current, next))
  }

  return (
    <AuthShell>
      <h2>گذرواژه‌تان را عوض کنید</h2>
      <p className="muted" style={{ marginBottom: 'var(--space-5)' }}>
        این گذرواژه را افرا برایتان گذاشته. قبل از ادامه، گذرواژهٔ تازه‌ای بگذارید که فقط خودتان بدانید.
      </p>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="pw-current">گذرواژهٔ فعلی</label>
          <input id="pw-current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="pw-next">گذرواژهٔ تازه (دست‌کم ۱۲ نویسه)</label>
          <input id="pw-next" type="password" autoComplete="new-password" required minLength={12} value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="pw-repeat">دوباره گذرواژهٔ تازه</label>
          <input id="pw-repeat" type="password" autoComplete="new-password" required minLength={12} value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </div>
        {(mismatch || error) && (
          <div className="notice notice--error" style={{ marginBottom: 'var(--space-4)' }}>
            {mismatch ? 'دو بار گذرواژهٔ تازه یکی نیست.' : error}
          </div>
        )}
        <button type="submit" className="btn" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'در حال ذخیره…' : 'ذخیرهٔ گذرواژه'}
        </button>
      </form>
      <button type="button" className="btn btn--quiet btn--sm" style={{ marginTop: 'var(--space-4)' }} onClick={onSignOut}>
        خروج
      </button>
    </AuthShell>
  )
}
